import dayjs from 'dayjs';
import { PARKED_VEHICLE_CATEGORY } from '~/features/monthly/constants/monthlyApplications';

/** 分類番号で許可されるアルファベット */
const ALLOWED_CLASSIFICATION_ALPHABETS = 'ACFHKLMPXY';

export const useValidation = () => {
  /**
   * 文字列が有効なカラーコードか判定する
   * @params {string} color
   * @example '#ffffff': true, '#fff': true, 'ff': false
   */
  const isColorCode = (color: string) => {
    return color.match(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/) !== null;
  };

  /**
   * 渡されたデータが自然数か判定する
   * @param {unknown} value
   */
  const isNatural = (value: unknown): boolean => {
    return typeof value === 'boolean' ||
      !Number.isInteger(Number(value)) ||
      Number(value) <= 0
      ? false
      : true;
  };

  /**
   * 渡された文字列がURLか判定する
   * @param {string} str
   */
  const isUrl = (str: string): boolean => {
    try {
      new URL(str);
      return true;
    } catch (e) {
      return false;
    }
  };

  /**
   * 渡された文字列の配列が携帯電話番号かを判定する
   * 070/080/090で始まる番号のみ有効
   * @param {array} value
   */
  const isValidMobilePhone = (
    value: (string | undefined)[] | undefined,
  ): boolean => {
    // undefined の場合は任意のためtrueを返す
    if (!value) return true;

    // valueが配列であることを確認
    // c8 ignore next
    if (!Array.isArray(value)) return false;

    // 配列の要素が3つであることを確認
    if (value.length !== 3) return false;

    const [areaCode, prefix, suffix] = value;

    // すべてが空の場合は任意のためtrueを返す
    if (!areaCode && !prefix && !suffix) return true;

    // それぞれが入力されているかを確認
    if (!areaCode || !prefix || !suffix) return false;

    // 0A0特番の携帯電話が有効かどうかを確認 (070, 080, 090のいずれか)
    const isValidAreaCode = ['070', '080', '090'].includes(areaCode);

    // 4桁の番号かを確認
    const isValidPrefix = /^\d{4}$/.test(prefix);
    const isValidSuffix = /^\d{4}$/.test(suffix);

    return isValidAreaCode && isValidPrefix && isValidSuffix;
  };

  /**
   * 渡された文字列の配列が固定電話番号かを判定する
   * 2桁4桁4桁、3桁3桁4桁、4桁2桁4桁の形式を有効とする
   * @param {array} value
   */
  const isValidLandlinePhone = (
    value: (string | undefined)[] | undefined,
  ): boolean => {
    // undefined の場合は任意のためtrueを返す
    if (!value) return true;

    // valueが配列であることを確認
    // c8 ignore next
    if (!Array.isArray(value)) return false;

    // 配列の要素が3つであることを確認
    if (value.length !== 3) return false;

    const [areaCode, prefix, suffix] = value;

    // すべてが空の場合は任意のためtrueを返す
    if (!areaCode && !prefix && !suffix) return true;

    // 全て入力されているかを確認
    if (!areaCode || !prefix || !suffix) return false;

    // 市外局番のチェック (2-4桁)
    const isValidAreaCode = /^\d{2,5}$/.test(areaCode);

    // 市内局番のチェック (2-4桁)
    const isValidPrefix = /^\d{1,4}$/.test(prefix);

    // 個別番号のチェック (4桁)
    const isValidSuffix = /^\d{4}$/.test(suffix);

    // パターン検証 (2桁4桁4桁、3桁3桁4桁、4桁2桁4桁)
    const isValidPattern =
      (areaCode.length === 2 && prefix.length === 4) ||
      (areaCode.length === 3 && prefix.length === 3) ||
      (areaCode.length === 4 && prefix.length === 2) ||
      (areaCode.length === 5 && prefix.length === 1);

    return isValidAreaCode && isValidPrefix && isValidSuffix && isValidPattern;
  };

  const isValidPhoneNumber = (
    value: (string | undefined)[] | undefined,
  ): boolean => {
    // undefined の場合は任意のためtrueを返す
    if (!value) return true;
    return isValidLandlinePhone(value) || isValidMobilePhone(value);
  };

  /**
   * 渡されたデータが有効な日付かを判定する
   * @param {number[] | string} value - 日付データ（[年, 月, 日]の配列または"YYYY-MM-DD"形式の文字列）
   */
  const isValidDate = (value: any[] | string | undefined): boolean => {
    if (!value) return false;

    if (Array.isArray(value)) {
      // 空配列の場合は任意としてtrueを返す
      if (value.every((x) => !x)) return true;

      const [year, _month, _day] = value;

      if (
        !Number.isInteger(year) ||
        !Number.isInteger(_month) ||
        !Number.isInteger(_day)
      )
        return false;

      const month = _month.toString().padStart(2, '0');
      const day = _day.toString().padStart(2, '0');

      return dayjs(`${year}${month}${day}`, 'YYYYMMDD', true).isValid();
    } else {
      return dayjs(value, 'YYYY-MM-DD', true).isValid();
    }
  };

  /**
   * 日本語（ひらがな・カタカナ・漢字）、アルファベットのみ許可
   * @param {string} value
   */
  const isValidName = (value: string | undefined) => {
    if (!value) return true;
    return /^[\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Han}a-zA-Z\s]+$/u.test(
      value,
    );
  };

  /**
   * カタカナのみ許可
   * @param {string} value
   * @returns
   */
  const isValidKana = (value: string | undefined) => {
    if (!value) return true;
    return /^[\p{Script=Katakana}\sー]+$/u.test(value);
  };

  /**
   * ひらがなのみ許可
   * @param {string} value
   */
  const isValidHiragana = (value: string | undefined) => {
    if (!value) return true;
    return /^[\p{Script=Hiragana}\sー]+$/u.test(value);
  };

  /**
   * 車両ナンバー配列のindex 1（分類番号）以外の要素が入力されているかをチェック
   * @param {array} value - 車両ナンバー配列 [地域, 分類番号, ひらがな, 一連指定番号]
   * @returns バリデーション結果
   */
  const isVehicleNumberRequired = (
    value: (string | null | undefined)[] | undefined,
  ): boolean => {
    // 配列が存在するかチェック
    if (!value || !Array.isArray(value)) return false;
    // index 1（分類番号）以外の要素が入力されているかチェック
    return value.every((v, index) => {
      if (index === 1) return true; // 分類番号は任意
      return v !== null && v !== undefined && v !== '';
    });
  };

  /**
   * 納車予定日（年月日の配列）が入力されているかをチェック
   * @param {array} value - 納車予定日配列 [year, month, day]
   * @returns バリデーション結果
   */
  const isScheduledDeliveryDateRequired = (
    value: (number | null | undefined)[] | undefined,
  ): boolean => {
    // 配列が存在し、長さが3で、すべての要素が入力されているかチェック
    if (!value || !Array.isArray(value) || value.length !== 3) return false;
    return value.every((v) => v !== null && v !== undefined);
  };

  /**
   * 分類番号のバリデーション
   * @param {string | null | undefined} value - 分類番号
   * @param {string} parkedVehicleCategory - 駐車車両種別（'AUTOMOBILE' or 'BIKE'）
   * @param {boolean} isResubmission - 再提出時かどうか
   * @returns バリデーション結果（true: 有効, false: 無効）
   */
  const validateClassificationNumber = (
    value: string | null | undefined,
    parkedVehicleCategory?: string,
    isResubmission?: boolean,
  ): boolean => {
    const isEmpty = !value || value.trim() === '';

    // バイクの場合は任意（null または '' はOK、バリデーションスキップ）
    if (parkedVehicleCategory === PARKED_VEHICLE_CATEGORY.BIKE) {
      if (isEmpty) {
        return true;
      }
    }

    // 自動車の場合は必須（ただし再提出時は任意）
    if (parkedVehicleCategory === PARKED_VEHICLE_CATEGORY.AUTOMOBILE) {
      if (isEmpty && !isResubmission) {
        return false;
      }
      if (isEmpty && isResubmission) {
        return true;
      }
    }

    // その他のカテゴリまたはカテゴリ未指定で値が空の場合は任意としてtrueを返す
    if (isEmpty) {
      return true;
    }

    const trimmedValue = value.trim();

    // 文字数が3超過のチェック
    if (trimmedValue.length > 3) {
      return false;
    }

    // 許可形式: 数字と許可アルファベット（大文字のみ）の組み合わせで1〜3文字
    const validPattern = new RegExp(`^[0-9A-Z]{1,3}$`);

    return validPattern.test(trimmedValue);
  };

  /**
   * 車両ナンバー（4分割入力）のバリデーション
   * yupのtestメソッドで直接使用可能
   * @param {array} value - [地域, 分類番号, ひらがな, 一連指定番号]
   * @param {string} parkedVehicleCategory - 駐車車両種別（'AUTOMOBILE' or 'BIKE'）
   * @param {boolean} isResubmission - 再提出時かどうか
   * @returns バリデーション結果
   */
  const validateVehicleNumber = function (
    value: (string | null | undefined)[] | undefined,
    parkedVehicleCategory?: string,
    isResubmission?: boolean,
  ) {
    const values = (value || []) as (string | null | undefined)[];
    const area = values[0];
    const classificationNumber = values[1];
    const hiragana = values[2];
    const serial = values[3];

    // 全ての入力が空の場合は任意のためtrueを返す
    if (
      (!area || area.trim() === '') &&
      (!classificationNumber || classificationNumber.trim() === '') &&
      (!hiragana || hiragana.trim() === '') &&
      (!serial || serial.trim() === '')
    ) {
      return true;
    }

    // 地域、ひらがな、一連指定番号は常に必須
    if (
      !area ||
      area.trim() === '' ||
      !hiragana ||
      hiragana.trim() === '' ||
      !serial ||
      serial.trim() === ''
    ) {
      return false;
    }

    // 地域の桁数チェック（1-4文字）
    const trimmedArea = area.trim();
    if (trimmedArea.length < 1 || trimmedArea.length > 4) {
      return false;
    }

    // ひらがなの文字数チェック（1文字）
    const trimmedHiragana = hiragana.trim();
    if (trimmedHiragana.length !== 1) {
      return false;
    }

    // ひらがなのバリデーション
     
    if (!isValidHiragana(hiragana)) {
      return false;
    }

    // 一連番号のバリデーション
    const serialValue = serial.trim();

    // 使用可能文字チェック（数字またはハイフンのみ）
    if (!/^[\d-]+$/.test(serialValue)) {
      return false;
    }

    // ハイフン除去後の数字列を取得
    const digitsOnly = serialValue.replace(/-/g, '');

    // 桁数制限チェック（ハイフン除去後の数字列が4桁以内、かつ1桁以上）
    if (digitsOnly.length === 0 || digitsOnly.length > 4) {
      return false;
    }

    // 分類番号のバリデーション
    return validateClassificationNumber(
      classificationNumber,
      parkedVehicleCategory,
      isResubmission,
    );
  };

  /**
   * RFC 5322にできる限り準拠したメールアドレスのバリデーション
   * 参考: https://tools.ietf.org/html/rfc5322
   *
   * @param email - 検証するメールアドレス
   * @returns バリデーション結果
   */
  const isValidEmail = (email: string): boolean => {
    if (!email || email.trim() === '') {
      return false;
    }

    // 基本的な長さ制限（RFC 5321）
    if (email.length > 254) {
      return false;
    }

    // @ の存在確認
    const atIndex = email.indexOf('@');
    const lastAtIndex = email.lastIndexOf('@');

    // @ が存在しない、または複数存在する場合は無効
    if (atIndex === -1 || atIndex !== lastAtIndex) {
      return false;
    }

    // @ の前後の部分を分離
    const localPart = email.substring(0, atIndex);
    const domainPart = email.substring(atIndex + 1);

    // ローカル部とドメイン部が空でないことを確認
    if (localPart.length === 0 || domainPart.length === 0) {
      return false;
    }

    // ローカル部の長さ制限（RFC 5321）
    if (localPart.length > 64) {
      return false;
    }

    // ドメイン部の長さ制限（RFC 5321）
    // (email.length > 254 の検証が先行するため、この分岐は到達不可能)
    /* v8 ignore start */
    if (domainPart.length > 253) {
      return false;
    }
    /* v8 ignore stop */

    // 連続するドットの禁止
    if (localPart.includes('..') || domainPart.includes('..')) {
      return false;
    }

    // ローカル部の最初と最後がドットであることを禁止
    if (localPart.startsWith('.') || localPart.endsWith('.')) {
      return false;
    }

    // ドメイン部の最初と最後がドットであることを禁止
    if (domainPart.startsWith('.') || domainPart.endsWith('.')) {
      return false;
    }

    // ローカル部の文字制限（RFC 5322の簡略版）
    const localPartRegex = /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~.-]+$/;
    if (!localPartRegex.test(localPart)) {
      return false;
    }

    // ドメインの各ラベルの検証
    const domainLabels = domainPart.split('.');

    // 少なくとも2つのラベル（例：example.com）が必要
    if (domainLabels.length < 2) {
      return false;
    }

    for (const label of domainLabels) {
      // ラベルの長さ制限（RFC 1035）
      if (label.length === 0 || label.length > 63) {
        return false;
      }

      // ラベルの最初と最後はハイフンであってはならない
      if (label.startsWith('-') || label.endsWith('-')) {
        return false;
      }

      // ラベルは英数字とハイフンのみ
      if (!/^[a-zA-Z0-9-]+$/.test(label)) {
        return false;
      }
    }

    // 最後のラベル（TLD）は少なくとも2文字必要
    const tld = domainLabels[domainLabels.length - 1];
    if (tld.length < 2 || !/^[a-zA-Z]+$/.test(tld)) {
      return false;
    }

    return true;
  };

  return {
    isColorCode,
    isNatural,
    isUrl,
    isValidMobilePhone,
    isValidLandlinePhone,
    isValidPhoneNumber,
    isValidDate,
    isValidName,
    isValidKana,
    isValidHiragana,
    isVehicleNumberRequired,
    isScheduledDeliveryDateRequired,
    validateClassificationNumber,
    validateVehicleNumber,
    isValidEmail,
  };
};
